import React from 'react';
import useModalStore from '../../stores/modalStore';
import useEventStore from "../../stores/eventStore";
import useUserStore from "../../stores/userStore";
import { hasPermission } from "../../utils/permissions";
import {fetchUserEventList} from "../../services/eventService";
import { DollarSign, ShieldCheck, UserPlus } from "lucide-react";
import useSettingsStore from "../../stores/settingsStore";
import AddEmployeeModal from "../modals/AddEmployeeModal";

const statusBadge = (status) => {
    const base = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap";
    switch (status) {
        case "on clock":
            return `${base} bg-emerald-100 text-emerald-800`;
        case "on lunch":
            return `${base} bg-amber-100 text-amber-800`;
        case "off clock":
            return `${base} bg-gray-100 text-gray-600`;
        case "sick":
            return `${base} bg-purple-100 text-purple-800`;
        case "time off":
            return `${base} bg-blue-100 text-blue-800`;
        default:
            return `${base} bg-gray-100 text-gray-600`;
    }
};

const EmployeeStatusTable = () => {
    const openModal = useModalStore(state => state.openModal);
    const { selectedEvent, setSelectedEvent } = useEventStore();
    const { employees, setEmployeeEvents } = useUserStore();
    const piiClass = useSettingsStore(s => s.blurPII) ? 'blur-pii' : '';

    if (!hasPermission('view_team_status')) return null;

    const activeEmployees = employees.filter((user) => user.end_date == null);

    return (
        <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {activeEmployees.map((user) => (
                    <div
                        key={user.user_id}
                        className="bg-gray-50 hover:bg-white border border-gray-200/80 rounded-xl p-4 flex flex-col gap-2.5 transition-colors cursor-pointer group"
                        onClick={async () => {
                            openModal("eventListModal", { userId: user.user_id });
                            await fetchUserEventList(user.user_id, setEmployeeEvents, "status table card click");
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className={`text-sm font-bold text-gray-800 group-hover:text-indigo-700 transition-colors ${piiClass}`}>
                                    {user.first_name}
                                </h4>
                                <p className="text-xs text-gray-400">{user.position} &middot; ID {user.user_id}</p>
                            </div>
                            <span className={statusBadge(user.status)}>{user.status}</span>
                        </div>

                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedEvent({ ...selectedEvent, user_id: user.user_id });
                                    openModal("paySummaryModal", { userId: user.user_id });
                                }}
                                className="!flex-1 !flex !items-center !justify-center !gap-1 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-white !border !border-gray-200 !rounded-lg hover:!bg-indigo-50 hover:!text-indigo-700 hover:!border-indigo-200 !transition-colors"
                            >
                                <DollarSign size={13} /> Pay
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openModal("esstReportModal", { userId: user.user_id, employeeName: user.first_name });
                                }}
                                className="!flex-1 !flex !items-center !justify-center !gap-1 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-white !border !border-gray-200 !rounded-lg hover:!bg-teal-50 hover:!text-teal-700 hover:!border-teal-200 !transition-colors"
                            >
                                <ShieldCheck size={13} /> ESST
                            </button>
                        </div>
                    </div>
                ))}

                {/* Add Employee Card */}
                <div
                    className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors min-h-[100px]"
                    onClick={() => openModal("addEmployeeModal")}
                >
                    <UserPlus size={24} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">Add Employee</span>
                </div>
            </div>

            <AddEmployeeModal />
        </div>
    );
};

export default EmployeeStatusTable;
